// The ⚙ panel for one transaction field — opened from a form field or a grid
// header in Customize mode. Two sections: how the field shows ON THE FORM
// (label, visible, editable, default, group) and IN THE LIST / grid (column
// heading, visible, freeze up to it). Changes are HELD in the panel and saved
// together by Done; Esc or a click outside closes it and drops them. A
// mandatory field cannot be hidden or locked until it has a default value.

import { useMemo, useRef, useState } from 'react'
import type { ColumnDef, SqlModel } from '../txn/types'
import { isHidden, isListHidden, listOrderedColumns } from '../txn/utils'
import { groupColumns } from '../txn/FormRenderer'
import { overlayLeafPaths, type CustomizeDiff, type CustomizeSession } from './session'
import {
  appOwnedReason, colPath, columnKey, coreColumn, formPath, groupKey, leafDiff, modelForm, resetLeaf, setLeaf, truthy,
} from './txnOps'
import {
  AnchoredPanel, PanelHeader, PanelNote, PanelRow, PanelSection, PanelText, Switch,
  VIOLET, VIOLET_LINE, VIOLET_SOFT,
} from './ui'
import { tw } from '../testHooks'
import { Loader2, RotateCcw } from 'lucide-react'

/** A change held in the panel until Done: a value, or back to standard. */
type Pending = { reset: true } | { reset?: false; value: unknown }

export interface TxnFieldPanelProps {
  session: CustomizeSession
  core: unknown
  form: SqlModel
  formNo: string
  colKey: string
  /** The header (list) form or a child form (its line grid). */
  gridLabel: string
  anchor: HTMLElement | null
  onClose: () => void
  /** Open the additional-attributes setup at this column. Absent ⇒ the
   *  attribute row says where it is set up, without a button. */
  onOpenUdaSetup?: (formNo: string, attrKey: string) => void
}

export default function TxnFieldPanel({
  session, core, form, formNo, colKey, gridLabel, anchor, onClose, onOpenUdaSetup,
}: TxnFieldPanelProps) {
  const col = useMemo(
    () => (form.columns || []).map((w) => w.column).find((c) => c && columnKey(c) === colKey),
    [form, colKey],
  ) as ColumnDef | undefined
  const std = coreColumn(core, formNo, colKey)
  const [warn, setWarn] = useState<string | null>(null)
  // Overlay path → the change held for it, plus what each one was (for the
  // Undo label). A ref as well as state: Done can follow straight on from a
  // text box committing on blur, in the same gesture.
  const [pending, setPendingState] = useState<Record<string, Pending>>({})
  const pendingRef = useRef(pending)
  const labelsRef = useRef<string[]>([])
  const [applying, setApplying] = useState(false)
  const [refused, setRefused] = useState(false)
  if (!col) return null

  // An additional attribute is not an overlay leaf: it is defined (label,
  // rules, placement) in the additional-attributes setup, never here.
  if (col.uda && !col.uda.driver) {
    const name = String(col.heading || col.uda.key || colKey)
    return (
      <AnchoredPanel anchor={anchor} onClose={onClose} label={name} width={420}>
        <PanelHeader title={name} sub="Additional attribute" saved={false} />
        <div style={{ padding: '10px 16px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <PanelNote>Additional attribute: set up under Additional attributes.</PanelNote>
          {onOpenUdaSetup && (
            <button type="button" onClick={() => { onClose(); onOpenUdaSetup(formNo, String(col.uda?.key || '')) }}
              {...tw('action', 'uda-setup')}
              style={{
                alignSelf: 'flex-start', height: 34, padding: '0 14px', borderRadius: 6, cursor: 'pointer',
                border: `1px solid ${VIOLET_LINE}`, background: VIOLET_SOFT, color: VIOLET,
                fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              }}>Open Additional attributes</button>
          )}
        </div>
      </AnchoredPanel>
    )
  }

  const p = (leaf: string) => colPath(formNo, colKey, leaf)
  const stdOf = (leaf: string) => (std as Record<string, unknown> | undefined)?.[leaf]
  const label = String(col.heading || col.name || colKey)
  const frozenPath = formPath(formNo, 'frozen_columns')

  // Hold a change instead of saving it.
  const stage = (diff: CustomizeDiff, what: string) => {
    const next = { ...pendingRef.current }
    for (const s of diff.set) next[s.path] = { value: s.value }
    for (const r of diff.reset) next[r] = { reset: true }
    pendingRef.current = next
    labelsRef.current = [...labelsRef.current, what]
    setPendingState(next)
    setRefused(false)
  }
  const apply = (leaf: string, value: unknown, what: string) =>
    stage(leafDiff(p(leaf), value, stdOf(leaf)), `${what} · ${label}`)
  const resetTo = (leaf: string, what: string) => stage(resetLeaf(p(leaf)), `${what} · ${label}`)
  const custom = (leaf: string) => {
    const held = pending[p(leaf)]
    return held ? !held.reset : session.isCustomized(p(leaf))
  }

  // What the panel shows: the field as it is, with the held changes on top.
  const shown = { ...col } as ColumnDef & Record<string, unknown>
  for (const [path, held] of Object.entries(pending)) {
    if (!path.startsWith(p(''))) continue
    const leaf = path.slice(p('').length)
    shown[leaf] = held.reset ? stdOf(leaf) : held.value
  }
  const owned = appOwnedReason(col)
  const mandatory = truthy(col.mandatory)
  const hasDefault = String(shown.default_value ?? '').trim() !== ''
  const changes = Object.keys(pending).length

  const done = async () => {
    const held = pendingRef.current
    const paths = Object.keys(held)
    if (!paths.length) { onClose(); return }
    const diff: CustomizeDiff = { set: [], reset: [] }
    for (const path of paths) {
      const h = held[path]
      if (h.reset) diff.reset.push(path)
      else diff.set.push({ path, value: h.value })
    }
    const labels = labelsRef.current
    setApplying(true)
    try {
      const ok = await session.apply(diff, labels.length === 1 ? labels[0] : `Change · ${label}`)
      if (ok) onClose()
      else setRefused(true)
    } finally {
      setApplying(false)
    }
  }

  const guardMandatory = (next: { hide?: boolean; lock?: boolean }) => {
    if (!mandatory || hasDefault || owned) return true
    const hidden = next.hide ?? isHidden(shown)
    const locked = next.lock ?? truthy(shown.protect)
    if (hidden || locked) {
      setWarn(`${label} is mandatory. Give it a default value first — otherwise nobody can fill it in.`)
      return false
    }
    return true
  }

  // Groups this form has (for the "Group" picker), in display order.
  const groups = groupColumns(
    (form.columns || []).map((w) => w.column).filter(Boolean) as ColumnDef[], false, [], form.group_settings,
  ).map(([g]) => g)
  const groupLabel = (g: string) =>
    String(form.group_settings?.[groupKey(g)]?.label ?? '').trim()
      || (g === 'Others' ? 'Other Information' : g)

  // The list / grid: where this column sits, for "freeze up to here".
  const listCols = listOrderedColumns(form)
  const listIdx = listCols.findIndex((c) => columnKey(c) === colKey)
  const heldFrozen = pending[frozenPath]
  const frozen = Number(heldFrozen
    ? (heldFrozen.reset ? modelForm(core, formNo)?.frozen_columns : heldFrozen.value)
    : form.frozen_columns) || 0
  const frozenHere = listIdx >= 0 && frozen === listIdx + 1

  return (
    <AnchoredPanel anchor={anchor} onClose={onClose} label={label} width={420}>
      <PanelHeader title={label}
        sub={`${col.db_name || col.name}${mandatory ? ' · mandatory' : ''}${truthy(col.key) ? ' · key field' : ''}`}
        saved={false} />
      <div style={{ padding: '10px 16px 6px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {owned && <PanelNote>{owned}. You can rename, move, hide or freeze it, but you can't make it editable or set its value.</PanelNote>}

        <PanelSection title="On the form" />
        <PanelText label="Label" value={String(shown.heading ?? '')} standard={String(std?.heading ?? '')}
          customized={custom('heading')} onReset={() => resetTo('heading', 'Label')}
          onCommit={(v) => apply('heading', v.trim() || String(std?.heading ?? ''), 'Label')} />
        <PanelRow title="Show on form" hint="Hidden fields move to the tray under the form">
          <Switch label="Show on form" on={!isHidden(shown)} onChange={(on) => {
            if (!on && !guardMandatory({ hide: true })) return
            setWarn(null)
            apply('hidden', !on, on ? 'Show' : 'Hide')
          }} />
        </PanelRow>
        <PanelRow title="Editable" hint={owned ? 'Fixed by the application' : 'Off makes it read-only for your organization'} dim={!!owned}>
          <Switch label="Editable" disabled={!!owned} on={!truthy(shown.protect)} onChange={(on) => {
            if (!on && !guardMandatory({ lock: true })) return
            setWarn(null)
            apply('protect', !on, on ? 'Editable' : 'Read-only')
          }} />
        </PanelRow>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          <PanelText label="Default value" value={owned ? '' : String(shown.default_value ?? '')}
            placeholder={owned ? 'Set by the application' : 'e.g. a value, or today()'} disabled={!!owned}
            customized={custom('default_value')}
            onReset={() => resetTo('default_value', 'Default')}
            onCommit={(v) => { setWarn(null); apply('default_value', v, 'Default') }} />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Group</span>
            <select value={groupKey(shown.group_name)} onChange={(e) => {
              const target = groups.find((g) => groupKey(g) === e.target.value)
              const raw = (form.columns || []).map((w) => w.column)
                .find((c) => c && groupKey(c.group_name) === e.target.value)?.group_name
              if (target != null) apply('group_name', raw ?? '', 'Group')
            }} style={{
              height: 34, boxSizing: 'border-box', borderRadius: 6, padding: '0 8px', fontFamily: 'inherit',
              border: `1px solid ${custom('group_name') ? '#7c3aed' : 'var(--color-border, #d5dae1)'}`,
              background: 'var(--color-bg, #fff)', color: 'var(--color-text, #111827)', fontSize: 15,
            }}>
              {groups.map((g) => <option key={groupKey(g)} value={groupKey(g)}>{groupLabel(g)}</option>)}
            </select>
          </label>
        </div>
        {warn && <PanelNote tone="warn">{warn} Not saved.</PanelNote>}

        <PanelSection title={`In ${gridLabel}`} />
        <PanelText label="Column heading" value={String(shown.list_heading ?? '')}
          placeholder={`Same as the label (${label})`} customized={custom('list_heading')}
          onReset={() => resetTo('list_heading', 'Column heading')}
          onCommit={(v) => apply('list_heading', v.trim(), 'Column heading')} />
        <PanelRow title={`Show in ${gridLabel}`} hint="A column of the grid">
          <Switch label="Show in list" on={!isListHidden(shown)}
            onChange={(on) => stage(setLeaf(p('list_hidden'), !on), `${on ? 'Show' : 'Hide'} column · ${label}`)} />
        </PanelRow>
        <PanelRow title="Freeze up to this column" hint={listIdx < 0 ? 'Show it in the grid first' : 'It and every column before it stay in view'} dim={listIdx < 0}>
          <Switch label="Freeze up to this column" disabled={listIdx < 0} on={frozenHere}
            onChange={(on) => stage(setLeaf(frozenPath, on ? listIdx + 1 : 0),
              on ? `Freeze after ${label}` : 'Unfreeze')} />
        </PanelRow>
      </div>
      {refused && (
        <div style={{ padding: '0 16px' }}>
          <PanelNote tone="warn">Not saved. {session.errors.join(' · ') || 'The change was refused.'}</PanelNote>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px 14px', borderTop: '1px solid var(--color-border, #f1f3f6)', marginTop: 6 }}>
        <button type="button" onClick={() => {
            // Every customized leaf of the field, held like any other change.
            const prefix = p('').slice(0, -1)
            const saved = overlayLeafPaths(session.overlay)
              .filter((x) => x === prefix || x.startsWith(`${prefix}.`))
            const held = Object.keys(pendingRef.current).filter((x) => x.startsWith(`${prefix}.`))
            setWarn(null)
            stage({ set: [], reset: Array.from(new Set([...saved, ...held])) }, `Reset · ${label}`)
          }}
          disabled={!session.countCustomized(p('').slice(0, -1)) && !Object.keys(pending).some((x) => x.startsWith(p('')))}
          style={{ border: 0, background: 'transparent', color: '#6d28d9', cursor: 'pointer', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <RotateCcw size={13} />Reset this field
        </button>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' }}>
            {changes ? 'Click outside or Esc to discard' : 'Esc or click outside to close'}
          </span>
          <button type="button" onClick={() => { void done() }} disabled={applying}
            {...tw('action', 'customize-panel-done')}
            style={{
              height: 32, padding: '0 16px', borderRadius: 6, border: 0, cursor: applying ? 'default' : 'pointer',
              background: VIOLET, color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 6, opacity: applying ? 0.75 : 1,
            }}>
            {applying && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
            Done
          </button>
        </span>
      </div>
    </AnchoredPanel>
  )
}
